'use client';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import {
  getDashboardBookings,
  getDashboardEarnings,
  getDashboardMetrics,
  transformDashboardMetrics
} from '@website/services/dashboard.service';

import { useDashboardFilters } from './useDashboardFilters';

export function useDashboardMetrics(activityId?: string) {
  const { getCurrentFilters } = useDashboardFilters([]);
  const filters = getCurrentFilters();

  const apiFilters = {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    activityId: activityId || undefined
  };

  return useQuery({
    queryKey: ['dashboard', 'metrics', normalizeFilters(apiFilters)],
    queryFn: async () => {
      try {
        const response = await getDashboardMetrics(apiFilters);
        return transformDashboardMetrics(response);
      } catch (error) {
        throw new Error(
          `Failed to load dashboard metrics: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3
  });
}

export function useDashboardBookings(activityId?: string) {
  const { getCurrentFilters } = useDashboardFilters([]);
  const filters = getCurrentFilters();

  const apiFilters = {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    activityId: activityId || undefined
  };

  return useQuery({
    queryKey: ['dashboard', 'bookings', normalizeFilters(apiFilters)],
    queryFn: async () => {
      try {
        return await getDashboardBookings(apiFilters);
      } catch (error) {
        throw new Error(
          `Failed to load booking chart data: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3
  });
}

export function useDashboardEarnings(activityId?: string) {
  const { getCurrentFilters } = useDashboardFilters([]);
  const filters = getCurrentFilters();

  const apiFilters = {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    activityId: activityId || undefined
  };

  return useQuery({
    queryKey: ['dashboard', 'earnings', normalizeFilters(apiFilters)],
    queryFn: async () => {
      try {
        return await getDashboardEarnings(apiFilters);
      } catch (error) {
        throw new Error(
          `Failed to load earnings chart data: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3
  });
}

function normalizeFilters(filters: Record<string, any>): Record<string, any> {
  const normalized: Record<string, any> = {};

  Object.keys(filters)
    .sort()
    .forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        normalized[key] = value;
      }
    });

  return normalized;
}

// ============ Suspense-enabled versions ============

/**
 * Suspense-enabled version of useDashboardMetrics.
 * Use this with React Suspense boundaries for cleaner loading states.
 */
export function useDashboardMetricsSuspense(activityId?: string) {
  const { getCurrentFilters } = useDashboardFilters([]);
  const filters = getCurrentFilters();

  const apiFilters = {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    activityId: activityId || undefined
  };

  return useSuspenseQuery({
    queryKey: ['dashboard', 'metrics', normalizeFilters(apiFilters)],
    queryFn: async () => {
      const response = await getDashboardMetrics(apiFilters);
      return transformDashboardMetrics(response);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}

/**
 * Suspense-enabled version of useDashboardBookings.
 */
export function useDashboardBookingsSuspense(activityId?: string) {
  const { getCurrentFilters } = useDashboardFilters([]);
  const filters = getCurrentFilters();

  const apiFilters = {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    activityId: activityId || undefined
  };

  return useSuspenseQuery({
    queryKey: ['dashboard', 'bookings', normalizeFilters(apiFilters)],
    queryFn: async () => {
      return await getDashboardBookings(apiFilters);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}

/**
 * Suspense-enabled version of useDashboardEarnings.
 */
export function useDashboardEarningsSuspense(activityId?: string) {
  const { getCurrentFilters } = useDashboardFilters([]);
  const filters = getCurrentFilters();

  const apiFilters = {
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    activityId: activityId || undefined
  };

  return useSuspenseQuery({
    queryKey: ['dashboard', 'earnings', normalizeFilters(apiFilters)],
    queryFn: async () => {
      return await getDashboardEarnings(apiFilters);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}
